// pages/sections/NonstandardJobsSection.js
// Nonstandard job names — Manager-only review. Lists open jobs whose names look
// nonstandard (duplicate "(n)" suffix, stray whitespace, or off their template)
// for jobs where the caller is Job Manager (admins see firm-wide).
// Actions per row:
//   * close and no roll   (audit log only; records the intent AND completes the row)
//   * conform name        (audit log only; records a rename to the standard name AND completes the row)
//   * mark reviewed       (audit log only)
//   * mark NLC            (the job's whole client leaves every section)
// A close, rename or review can be undone by the manager (switching the Reviewed toggle off
// resets the row) until the overnight refresh has applied the intent to PE; after that only
// an admin Unmark reopens it. Fixing the name in PE makes the row drop off on the next mirror sync.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../auth/AuthContext';
import { usePeriod } from '../../hooks/usePeriod';
import { useSectionData } from '../../hooks/useSectionData';
import { useRowMutations } from '../../hooks/useRowMutations';
import LastRefreshed from '../../components/LastRefreshed';
import ToggleSwitch from './ToggleSwitch';
import MirrorNote from './MirrorNote';
import PeJobLink from './PeJobLink';
import ReviewPagination, { REVIEW_PAGE_SIZE as PAGE_SIZE } from './ReviewPagination';
import DisengagementModal from './DisengagementModal';
import NlcRequestedBadge from './NlcRequestedBadge';
import {
  nlcModalProps, announceNlcOutcome, nlcLeavesList, requestFlagFrom, withdrawNlcRequest, sweepRelated, patchClientRows,
} from './nlcRequestUi';
import { fmtDate, fmtHours, doneTip, postJson, postAdminUnmark, patchRowIn, sortDoneLast } from './reviewUtil';
import './ReviewTable.css';
import './NonstandardJobsSection.css';

const SECTION_KEY = 'nonstandard-jobs';

const REASON_OPTIONS = [
  { value: '',             label: 'All Reasons' },
  { value: 'duplicate',    label: 'Duplicate' },
  { value: 'whitespace',   label: 'Extra spaces' },
  { value: 'off-template', label: 'Off-template' },
];

// Render a job name with stray whitespace made visible: spaces that are
// leading, trailing, or part of a run of 2+ show as a highlighted middle-dot.
// Single interior spaces render normally.
function renderJobName(name) {
  const s = String(name ?? '');
  const out = [];
  let pos = 0;
  s.split(/(\s+)/).forEach((part, i) => {
    if (!part) return;
    const start = pos;
    pos += part.length;
    const isSpace = /^\s+$/.test(part);
    if (isSpace && (start === 0 || pos === s.length || part.length >= 2)) {
      [...part].forEach((_, j) => {
        out.push(<span key={`${i}-${j}`} className="nj-space" title="extra space">·</span>);
      });
    } else {
      out.push(<React.Fragment key={i}>{part}</React.Fragment>);
    }
  });
  return out;
}

function reasonChip(reason, row) {
  if (reason === 'duplicate') {
    return (
      <span key={reason} className="rt-chip nj-chip--dup"
            title="Name ends in a duplicate suffix; a twin job with the base name may exist.">
        Duplicate (n)
      </span>
    );
  }
  if (reason === 'whitespace') {
    return (
      <span key={reason} className="rt-chip nj-chip--ws"
            title="Name has leading, trailing, or doubled spaces.">
        Extra spaces
      </span>
    );
  }
  if (reason === 'off-template') {
    return (
      <span key={reason} className="rt-chip nj-chip--tmpl"
            title={row.templateName ? `Template: ${row.templateName}` : 'Name does not follow its template.'}>
        Off-template
      </span>
    );
  }
  return <span key={reason} className="rt-chip">{reason}</span>;
}

export default function NonstandardJobsSection() {
  const { email, isAdmin } = useAuth();
  const { period, periodActive } = usePeriod();

  const [search, setSearch]             = useState('');
  const [reasonFilter, setReasonFilter] = useState('');
  const [officeFilter, setOfficeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // '' | 'pending' | 'reviewed'
  const [page, setPage]                 = useState(1);
  const [nlcRow, setNlcRow]             = useState(null);      // row pending the NLC disengagement form
  const [nlcSaving, setNlcSaving]       = useState(false);
  const [nlcError, setNlcError]         = useState(null);      // last failed NLC submit — shown in the modal

  const fetcher = useCallback(async () => {
    if (!period) return null;
    const res = await fetch(`/api/diNonstandardJobs/${period}`, { credentials: 'include' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }, [period]);

  // Optimistic saves: ordered per job, never disable the controls, and a refetch can't overwrite an edit.
  const mutations = useRowMutations(r => r.jobIdx);
  const { pinned, clearPinned } = mutations;

  const { data, setData, initialLoading, error, lastRefreshedAt, freshness, silentRefetch }
    = useSectionData(fetcher, {
      deps: [period, email],
      heartbeat: { sections: [SECTION_KEY], period },
      reconcile: mutations.reconcile,
    });

  // Rows edited this session stay listed (e.g. under "Pending") until the user changes a filter.
  useEffect(() => { clearPinned(); }, [search, reasonFilter, statusFilter, officeFilter, clearPinned]);

  const fireAction = (jobIdx, action) => postJson(`/api/diNonstandardJobs/${period}`, { action, jobIdx });

  // Optimistic row patcher. Returns the snapshot row for revert.
  const patchRow = (jobIdx, updater) => patchRowIn(setData, jobIdx, updater);

  // Translate an action into an optimistic row patch.
  function applyActionLocally(row, action) {
    const review = row.review || {};
    if (action === 'mark-reviewed') {
      return { ...row, review: { ...review, reviewed: true, reviewedAt: new Date().toISOString(), reviewedBy: email } };
    }
    if (action === 'close-no-roll') {
      // Records the close intent AND completes the row in one step.
      return {
        ...row,
        review: { ...review, closeAction: 'close-no-roll', reviewed: true, reviewedAt: new Date().toISOString(), reviewedBy: email },
      };
    }
    if (action === 'conform-name') {
      // Records the rename intent AND completes the row in one step.
      return {
        ...row,
        review: { ...review, rename: { to: row.conformName }, reviewed: true, reviewedAt: new Date().toISOString(), reviewedBy: email },
      };
    }
    if (action === 'clear') {
      return { ...row, review: { ...review, closeAction: null } };
    }
    if (action === 'undo') {
      // Resets the row: close / rename intent and completion all go.
      return { ...row, review: { ...review, closeAction: null, rename: null, reviewed: false, reviewedAt: null, reviewedBy: null } };
    }
    return row;
  }

  async function postAction(jobIdx, action) {
    // The rendered row = its state before this edit (a setData updater may run later).
    const snapshot = (data?.rows || []).find(r => String(r.jobIdx) === String(jobIdx));
    patchRow(jobIdx, r => applyActionLocally(r, action));
    try {
      // Queued per job so rapid clicks reach the server in order; the controls stay live meanwhile.
      await mutations.run(jobIdx, () => fireAction(jobIdx, action), {
        revert: () => { if (snapshot) patchRow(jobIdx, () => snapshot); },
        refetch: silentRefetch,
      });
      toast.success(action === 'undo' ? 'Undone' : `Saved: ${action.replace(/-/g, ' ')}`);
    } catch (err) {
      toast.error(`Failed: ${err.message}`, { autoClose: 10000 });
    }
  }

  async function adminUnmark(jobIdx) {
    if (!isAdmin) return;
    const row = (data?.rows || []).find(r => String(r.jobIdx) === String(jobIdx));
    const label = row ? `${row.clientCode} — ${row.jobName}` : 'this job';
    if (!window.confirm(`Unmark ${label} as reviewed? A close already applied to PE is not changed.`)) return;

    // Override for a row PE has already applied (the manager's Undo is closed). The close
    // intent stays on record.
    const snapshot = row;
    patchRow(jobIdx, r => ({
      ...r,
      review: { ...(r.review || {}), reviewed: false, reviewedAt: null, reviewedBy: null },
    }));
    try {
      await mutations.run(jobIdx, () => postAdminUnmark(period, SECTION_KEY, jobIdx, 'manager'), {
        revert: () => { if (snapshot) patchRow(jobIdx, () => snapshot); },
        refetch: silentRefetch,
      });
      toast.success('Unmarked reviewed.');
    } catch (err) {
      toast.error(`Failed: ${err.message}`, { autoClose: 10000 });
    }
  }

  // Mark No Longer Client: the same disengagement form + server process as Client Data /
  // Stale Clients. The client's Partner (or an admin) marks it now: every job of that client leaves
  // every section. The Job Manager's mark FILES A REQUEST instead (outcome 'requested'): the jobs stay,
  // flagged "NLC requested", until the Partner confirms. Related parties picked in the form follow the
  // same per-client rules. On failure the modal stays open with its answers.
  async function markNlc(row, disengagement) {
    setNlcSaving(true);
    setNlcError(null);
    let j;
    try {
      // Queued behind any save still in flight for this job.
      j = await mutations.run(row.jobIdx, async () => {
        const res = await postJson(`/api/diNonstandardJobs/${period}`, { action: 'mark-nlc', jobIdx: row.jobIdx, disengagement });
        return res.json().catch(() => ({}));
      });
    } catch (err) {
      setNlcError(err.message);
      toast.error(`Couldn't mark ${row.clientCode} NLC: ${err.message}`, { autoClose: 10000 });
      setNlcSaving(false);
      return;
    }
    setNlcRow(null);
    setNlcSaving(false);
    const outcome = announceNlcOutcome(j, row.clientCode,
      `${row.clientCode} marked No Longer Client — all of its jobs leave every section.`);
    if (nlcLeavesList(outcome)) {
      setData(d => (d ? { ...d, rows: (d.rows || []).filter(r => String(r.contIndex) !== String(row.contIndex)) } : d));
    } else {
      patchClientRows(setData, row.contIndex, r => ({ ...r, nlcRequest: r.nlcRequest || requestFlagFrom(j, email) }));
    }
    await sweepRelated({
      disengagement, primaryContIndex: row.contIndex, primaryClientCode: row.clientCode, period,
      sourceSectionKey: SECTION_KEY,
    });
    silentRefetch();        // drop any related parties that were in this list
  }

  // The requester takes a pending request back: the badge goes at once (on every job of the
  // client); a failure restores it.
  async function withdrawNlc(row) {
    const req = row.nlcRequest;
    if (!req) return;
    patchClientRows(setData, row.contIndex, r => ({ ...r, nlcRequest: null }));
    try {
      await withdrawNlcRequest(req.id);
      toast.success(`Request for ${row.clientCode} withdrawn.`);
    } catch (err) {
      patchClientRows(setData, row.contIndex, r => ({ ...r, nlcRequest: req }));
      toast.error(`Couldn't withdraw the request for ${row.clientCode}: ${err.message}`, { autoClose: 10000 });
    }
  }

  const rows = useMemo(() => data?.rows || [], [data]);

  const offices = useMemo(
    () => [...new Set(rows.map(r => r.jobOffice))].filter(Boolean).sort(),
    [rows]
  );

  const filtered = useMemo(() => {
    let out = rows;
    if (search) {
      const s = search.toLowerCase();
      out = out.filter(r =>
        (r.clientCode || '').toLowerCase().includes(s) ||
        (r.clientName || '').toLowerCase().includes(s) ||
        (r.jobName || '').toLowerCase().includes(s)
      );
    }
    if (reasonFilter) out = out.filter(r => (r.reasons || []).includes(reasonFilter));
    if (isAdmin && officeFilter) out = out.filter(r => r.jobOffice === officeFilter);
    // Rows the user just edited stay put even if the edit moved them out of this status.
    const keep = r => pinned.has(String(r.jobIdx));
    if (statusFilter === 'pending')  out = out.filter(r => keep(r) || !r.review?.reviewed);
    if (statusFilter === 'reviewed') out = out.filter(r => keep(r) ||  r.review?.reviewed);
    return sortDoneLast(out, r => !!r.review?.reviewed);
  }, [rows, search, reasonFilter, officeFilter, statusFilter, isAdmin, pinned]);

  // Recomputed from rows (not data.summary) so optimistic toggles move the bar.
  // Must be declared BEFORE any early returns so hook count stays stable.
  const summary = useMemo(() => {
    const total    = rows.length;
    const reviewed = rows.filter(r => r.review?.reviewed).length;
    return { total, reviewed, pending: total - reviewed };
  }, [rows]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const paged      = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  if (initialLoading) return <div className="rt-loading">Loading nonstandard job names…</div>;
  if (error && !data) return <div className="rt-error">Error: {error}</div>;
  if (!data) return null;

  const readOnly = !periodActive;
  const mirror   = data.mirror;
  const pct = summary.total > 0 ? Math.round((summary.reviewed / summary.total) * 100) : 0;

  return (
    <div className="rt-page">
      <div className="rt-header">
        <div className="rt-title-block">
          <h2 className="rt-title">Nonstandard Job Names</h2>
          {readOnly && <span className="rt-readonly-badge">Read-only</span>}
          <LastRefreshed at={lastRefreshedAt} freshness={freshness} onRefresh={silentRefetch} />
        </div>
      </div>
      <p className="rt-subtitle">
        Open jobs whose names look nonstandard — a duplicate "(1)" suffix, stray spaces, or a name
        that doesn't follow its template. Fix the name in Practice Engine (the row drops off within
        a few minutes), mark it reviewed if it's intentional, or flag a duplicate to Close &amp; No Roll.
      </p>
      <MirrorNote mirror={mirror} staleHint="recent name fixes might not show yet." />

      <div className="rt-progress-section">
        <div className="rt-progress-header">
          <h3 className="rt-progress-title">{isAdmin ? 'Firm Progress' : 'Your Progress'}</h3>
          <span className="rt-progress-label-val">
            <span className="rt-progress-count">{summary.reviewed} / {summary.total}</span>
            <span className="rt-progress-divider">—</span>
            {pct}% Complete
          </span>
        </div>
        <div className="rt-progress-track">
          <div
            className="rt-progress-fill"
            style={{
              width: `${Math.min(100, Math.max(0, pct))}%`,
              background: pct >= 100 ? '#1a7a4a' : pct >= 50 ? '#e6a817' : '#c0392b',
            }}
          />
        </div>
      </div>

      <div className="rt-summary">
        <div className="rt-stat"><span className="rt-stat-val">{summary.total}</span><span className="rt-stat-lbl">{isAdmin ? 'Jobs' : 'Your Jobs'}</span></div>
        <div className="rt-stat rt-stat--pending"><span className="rt-stat-val">{summary.pending}</span><span className="rt-stat-lbl">Needs Review</span></div>
        <div className="rt-stat rt-stat--done"><span className="rt-stat-val">{summary.reviewed}</span><span className="rt-stat-lbl">Done</span></div>
      </div>

      <div className="rt-filters">
        <input className="rt-filter-input" placeholder="Search by client code, client name, or job…"
               value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        <select className="rt-filter-select" value={reasonFilter}
                onChange={e => { setReasonFilter(e.target.value); setPage(1); }}>
          {REASON_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select className="rt-filter-select" value={statusFilter}
                onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="reviewed">Done</option>
        </select>
        {isAdmin && (
          <select className="rt-filter-select" value={officeFilter}
                  onChange={e => { setOfficeFilter(e.target.value); setPage(1); }}>
            <option value="">All Offices</option>
            {offices.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        )}
      </div>

      <div className="rt-table-wrap">
        <table className="rt-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Job Name</th>
              <th title="Why the name was flagged: duplicate '(n)' suffix, extra spaces, or not following its template">Reasons</th>
              <th>Work Status</th>
              <th>Created</th>
              <th>Time</th>
              <th title="Job Partner (admins also see the Job Manager)">Job Partner</th>
              <th title="Flag the job to be closed without rolling to next year. Completes the row.">Close & No Roll</th>
              <th title="The name is intentional or already fixed in PE. Completes the row.">Reviewed</th>
              <th title="Mark this client No Longer Client — removes all of its jobs from every section.">NLC</th>
            </tr>
          </thead>
          <tbody>
            {paged.map(row => {
              const review      = row.review || {};
              const reviewed    = !!review.reviewed;
              const closeAction = review.closeAction;
              const reasons     = row.reasons || [];
              const twin        = row.twin;
              const reviewedTip = reviewed ? doneTip({ by: review.reviewedBy, at: review.reviewedAt }) : undefined;
              const applied     = !!review.appliedAt;   // close already applied to PE: Undo is closed
              const rename      = review.rename;

              return (
                <tr key={row.jobIdx} className={reviewed ? 'rt-row-reviewed' : ''}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{row.clientCode}</div>
                    <div style={{ fontSize: '0.75rem', color: '#666' }}>{row.clientName}</div>
                  </td>
                  <td>
                    <div className="nj-jobname">{renderJobName(row.jobName)}</div>
                    <PeJobLink jobIdx={row.jobIdx} />
                    {reasons.includes('off-template') && row.templateName && (
                      <div className="nj-template">Template: {row.templateName}</div>
                    )}
                    {rename ? (
                      applied ? (
                        <div className="nj-rename">Renamed to "{rename.to}" in PE</div>
                      ) : (
                        <div className="nj-rename" title="Applied in PE overnight. Switch the Reviewed toggle off to withdraw it.">
                          Renaming to "{rename.to}" — applies overnight
                        </div>
                      )
                    ) : (row.conformName && !reviewed && (
                      <div className="nj-conform">
                        <button
                          className="rt-btn"
                          disabled={readOnly || row.conformBlocked}
                          title={row.conformBlocked
                            ? `The client already has a job named "${row.conformName}" — fix it in PE or Mark Reviewed.`
                            : 'Records a rename to the standard name — applied in PE overnight — and marks the job reviewed.'}
                          onClick={() => postAction(row.jobIdx, 'conform-name')}>
                          Conform name → {row.conformName}
                        </button>
                      </div>
                    ))}
                    {twin && (
                      <div className={`nj-twin ${twin.isOpen ? 'nj-twin--open' : ''}`}
                           title={twin.isOpen ? 'Both jobs are open — a true duplicate.' : 'The twin job is already closed.'}>
                        Twin: <span className="nj-twin-name">{renderJobName(twin.jobName)}</span>
                        {' · '}<span className="nj-twin-tag">{twin.isOpen ? 'Open' : 'Closed'}</span>
                        {twin.workStatus ? ` · ${twin.workStatus}` : ''}
                        {' · '}created {fmtDate(twin.created)}
                        {' · '}{fmtHours(twin.hours)} h (last time {fmtDate(twin.lastTimeEntry)})
                      </div>
                    )}
                  </td>
                  <td>
                    {reasons.length > 0
                      ? reasons.map(reason => reasonChip(reason, row))
                      : <span className="rt-cell-muted">—</span>}
                  </td>
                  <td>{row.workStatus || <span className="rt-cell-muted">—</span>}</td>
                  <td>{fmtDate(row.jobCreated)}</td>
                  <td>
                    <div>{fmtHours(row.hours)} h</div>
                    <div style={{ fontSize: '0.75rem', color: '#666' }}>
                      {row.lastTimeEntry ? fmtDate(row.lastTimeEntry) : 'no time yet'}
                    </div>
                  </td>
                  <td>
                    <div>{row.jobPartner || <span className="rt-cell-muted">—</span>}</div>
                    {isAdmin && row.jobManager && (
                      <div className="rt-cell-muted" style={{ fontSize: '0.75rem' }}>JM: {row.jobManager}</div>
                    )}
                  </td>
                  <td>
                    <span title={applied
                      ? "This row's change was applied to PE overnight, so it can't be undone here (an admin can Unmark the row)."
                      : (closeAction === 'close-no-roll'
                        ? "Switch off to undo: withdraws the close and reopens this row. Available until it's applied to PE overnight."
                        : 'Flag this job to be closed in PE without rolling to next year (e.g. an unneeded duplicate). Recorded now, applied in PE overnight. Completes this row.')}>
                      <ToggleSwitch
                        variant="danger"
                        checked={closeAction === 'close-no-roll'}
                        disabled={readOnly || applied}
                        // Off = undo the row when it's complete; a plain withdraw when an admin
                        // Unmark has already reopened it.
                        onChange={checked => postAction(row.jobIdx, checked ? 'close-no-roll' : (reviewed ? 'undo' : 'clear'))}
                      />
                    </span>
                    {applied && (
                      <div className="nj-applied" title={`Applied to PE ${new Date(review.appliedAt).toLocaleString()}`}>
                        Applied to PE
                      </div>
                    )}
                  </td>
                  <td className="rt-mark-reviewed-cell" onClick={e => e.stopPropagation()}>
                    <span title={reviewed
                      ? `${reviewedTip}${applied ? (isAdmin ? ' — applied to PE; admin: toggle off to unmark' : ' — applied to PE') : ' — switch off to undo'}`
                      : "Mark reviewed — the name is intentional, or you've fixed it in PE. Completes this row."}>
                      <ToggleSwitch
                        checked={reviewed}
                        disabled={readOnly || (applied && !isAdmin)}
                        onChange={checked => {
                          if (checked && !reviewed) {
                            postAction(row.jobIdx, 'mark-reviewed');
                          } else if (!checked && reviewed) {
                            if (applied) adminUnmark(row.jobIdx);   // Undo is closed; admin override only
                            else postAction(row.jobIdx, 'undo');
                          }
                        }}
                      />
                    </span>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {row.nlcRequest ? (
                      <NlcRequestedBadge request={row.nlcRequest} onWithdraw={() => withdrawNlc(row)} />
                    ) : (
                      <ToggleSwitch variant="danger" checked={false} disabled={readOnly || nlcSaving}
                                    onChange={checked => { if (checked) { setNlcError(null); setNlcRow(row); } }} />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {summary.total === 0 && (
          <div className="rt-empty">No nonstandard job names — nothing to review.</div>
        )}
        {filtered.length === 0 && summary.total > 0 && (
          <div className="rt-empty">No jobs match.</div>
        )}
      </div>

      <ReviewPagination page={safePage} totalPages={totalPages} rowCount={filtered.length} onPage={setPage} />

      {nlcRow && (
        <DisengagementModal
          client={{ code: nlcRow.clientCode, name: nlcRow.clientName }}
          {...nlcModalProps()}
          submitting={nlcSaving}
          error={nlcError}
          onCancel={() => setNlcRow(null)}
          onConfirm={disengagement => markNlc(nlcRow, disengagement)}
        />
      )}
    </div>
  );
}
